import React from "react"
import PropTypes from "prop-types"
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
} from "@material-ui/core"
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons"
import styles from "./AdminSubscriptionDetailContainer.module.scss"

function AdminSubscriptionDetailContainer({ subscription, loading, error, onBack }) {
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Typography variant="h4" className={styles.title}>
            Error
          </Typography>
          <Typography variant="body1" className={styles.error}>
            {error}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            className={styles.backButton}
          >
            Back to List
          </Button>
        </div>
      </div>
    )
  }

  if (!subscription) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Typography variant="h4" className={styles.title}>
            Subscription not found
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            className={styles.backButton}
          >
            Back to List
          </Button>
        </div>
      </div>
    )
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#4caf50"
      case "paused":
        return "#ff9800"
      case "trial":
        return "#2196f3"
      default:
        return "#757575"
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            className={styles.backButton}
          >
            Back
          </Button>
        </Box>

        <Typography variant="h4" className={styles.title}>
          Subscription Details
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={styles.detailCard}>
              <CardContent>
                <Typography variant="h6" className={styles.sectionTitle}>
                  Company Information
                </Typography>
                <Box className={styles.detailRow}>
                  <Typography className={styles.label}>Company Name:</Typography>
                  <Typography className={styles.value}>{subscription.name || "-"}</Typography>
                </Box>
                <Box className={styles.detailRow}>
                  <Typography className={styles.label}>User Email:</Typography>
                  <Typography className={styles.value}>
                    {subscription.user_email || "-"}
                  </Typography>
                </Box>
                <Box className={styles.detailRow}>
                  <Typography className={styles.label}>Company Type:</Typography>
                  <Typography className={styles.value}>
                    {subscription.company_type || "-"}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card className={styles.detailCard}>
              <CardContent>
                <Typography variant="h6" className={styles.sectionTitle}>
                  Subscription Status
                </Typography>
                <Box className={styles.detailRow}>
                  <Typography className={styles.label}>Status:</Typography>
                  <Chip
                    label={subscription.status}
                    style={{
                      backgroundColor: getStatusColor(subscription.status),
                      color: "#fff",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                  />
                </Box>
                <Box className={styles.detailRow}>
                  <Typography className={styles.label}>Seats:</Typography>
                  <Typography className={styles.value}>{subscription.seats || 0}</Typography>
                </Box>
                <Box className={styles.detailRow}>
                  <Typography className={styles.label}>Extended Trial:</Typography>
                  <Typography className={styles.value}>
                    {subscription.extended_trial ? "Yes" : "No"}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card className={styles.detailCard}>
              <CardContent>
                <Typography variant="h6" className={styles.sectionTitle}>
                  Subscription Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box className={styles.detailRow}>
                      <Typography className={styles.label}>Purchase Date:</Typography>
                      <Typography className={styles.value}>
                        {subscription.subscription_purchase_date
                          ? new Date(
                              subscription.subscription_purchase_date,
                            ).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box className={styles.detailRow}>
                      <Typography className={styles.label}>Pause Date:</Typography>
                      <Typography className={styles.value}>
                        {subscription.subscription_pause_date
                          ? new Date(subscription.subscription_pause_date).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box className={styles.detailRow}>
                      <Typography className={styles.label}>Created At:</Typography>
                      <Typography className={styles.value}>
                        {subscription.created_at
                          ? new Date(subscription.created_at).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box className={styles.detailRow}>
                      <Typography className={styles.label}>Updated At:</Typography>
                      <Typography className={styles.value}>
                        {subscription.updated_at
                          ? new Date(subscription.updated_at).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card className={styles.detailCard}>
              <CardContent>
                <Typography variant="h6" className={styles.sectionTitle}>
                  Stripe Information
                </Typography>
                <Box className={styles.detailRow}>
                  <Typography className={styles.label}>Stripe Customer ID:</Typography>
                  <Typography className={styles.value}>
                    {subscription.stripe_customer_id || "-"}
                  </Typography>
                </Box>
                <Box className={styles.detailRow}>
                  <Typography className={styles.label}>Stripe Subscription ID:</Typography>
                  <Typography className={styles.value}>
                    {subscription.stripe_subscription_id || "-"}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

AdminSubscriptionDetailContainer.propTypes = {
  subscription: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    user_email: PropTypes.string,
    company_type: PropTypes.string,
    status: PropTypes.string,
    seats: PropTypes.number,
    extended_trial: PropTypes.bool,
    subscription_purchase_date: PropTypes.string,
    subscription_pause_date: PropTypes.string,
    stripe_customer_id: PropTypes.string,
    stripe_subscription_id: PropTypes.string,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
  }),
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onBack: PropTypes.func.isRequired,
}

AdminSubscriptionDetailContainer.defaultProps = {
  subscription: null,
  error: null,
}

export default AdminSubscriptionDetailContainer
